/**
 * Guday — Event Layer Tracking
 * Versão: 2.0 (13/Ago/2026) — reescrita para este tema
 *
 * Empurra eventos de CLIQUE para o window.dataLayer, de onde o GTM
 * (container GTM-NZWHJGTN) os transforma em eventos GA4.
 *
 *   Elemento com data-tracking="nome"  ─┐
 *   Controles do side cart (Halo)      ─┼─► tracking.js ─► dataLayer ─► GTM ─► GA4
 *   CTA de compra                      ─┘
 *
 * NÃO confundir com o funil das LPs (add_to_cart / begin_checkout), que cada LP
 * empurra por conta própria nos ups-*.js. Este arquivo é a camada de cliques.
 *
 * ─── Relação com a v1.2 do tema antigo ──────────────────────────────────────
 * É REESCRITA, não port. ~53% do arquivo antigo era inaplicável aqui:
 *   • bloco Tolstoy (~64 linhas) — o app não existe neste tema. Removido.
 *   • cart drawer Kaching (~150 linhas) — o carrinho aqui é o side cart do Halo.
 *     Todos os seletores foram remapeados contra o DOM real (ver CART_MAP).
 *
 * Três defeitos da v1.2 que NÃO foram portados:
 *   1. preventDefault() + eventCallback do GTM em todo link dentro de um
 *      container [data-tracking]. Isso punha o GTM no caminho crítico de cada
 *      clique de navegação: GTM lento, bloqueado por adblock ou com erro =
 *      navegação travada até o eventTimeout de 500ms. O ganho (garantir o
 *      evento antes do unload) não paga o risco de segurar a navegação do
 *      cliente. Hoje o navegador já entrega beacons pendentes no unload.
 *   2. Debounce GLOBAL de 300ms com um único `lastPush`: dois eventos distintos
 *      em seções distintas dentro da janela faziam o segundo sumir em silêncio.
 *      Agora o debounce é por NOME de evento.
 *   3. `target.href || target.closest("a") ? (...) : ""` — precedência de || sobre
 *      ?: fazia isso avaliar como `(a || b) ? ... : ...`. Funcionava por acidente.
 *
 * ─── Nomes de evento ────────────────────────────────────────────────────────
 * Preservados exatamente como na v1.2 (cart_abrir, product_cta, ...). Os
 * triggers de Custom Event no GTM foram criados para esses nomes; renomear aqui
 * exigiria refazê-los lá, e o sintoma de um trigger faltando é idêntico ao de
 * não ter feito nada: código certo, GA4 vazio.
 */

(function () {
  "use strict";

  if (window.__gdTrackingInit) return;   // idempotência: o arquivo pode ser incluído 2x
  window.__gdTrackingInit = true;

  window.dataLayer = window.dataLayer || [];

  /* ─── Config ──────────────────────────────────────────────────────────── */

  var DEBOUNCE_MS = 300;
  var MAX_TEXT_LENGTH = 100;

  // Quando o nome em data-tracking difere do nome do evento no GTM.
  var EVENT_MAP = {
    menu: "menu_click",
    footer: "footer_click"
  };

  // Parâmetros fixos por seção.
  var EXTRA_PARAMS = {
    menu: { menu_location: "header" },
    footer: { footer_location: "bottom" }
  };

  /* ─── Utilitários ─────────────────────────────────────────────────────── */

  function cleanText(str) {
    if (!str) return "";
    return String(str).replace(/\s+/g, " ").trim().substring(0, MAX_TEXT_LENGTH);
  }

  /* Debounce POR EVENTO (ver defeito 2 no cabeçalho). Um clique em "menu" não
     pode mais engolir um clique em "footer" que aconteça 100ms depois. */
  var lastPush = {};
  function shouldPush(eventName) {
    var now = Date.now();
    if (lastPush[eventName] && now - lastPush[eventName] < DEBOUNCE_MS) return false;
    lastPush[eventName] = now;
    return true;
  }

  function findClickable(target) {
    if (!target || !target.closest) return null;
    return target.closest("a, button, [role='button'], input[type='submit']");
  }

  /* href do link ancestral, se houver. Escrito sem o bug de precedência da v1.2. */
  function clickUrlOf(target) {
    if (!target || !target.closest) return "";
    var anchor = target.closest("a[href]");
    return anchor ? anchor.href || "" : "";
  }

  function push(payload) {
    try {
      window.dataLayer.push(payload);
    } catch (e) {
      /* nunca deixar o tracking quebrar a página */
    }
  }

  /* ─── 1) Motor genérico [data-tracking] ───────────────────────────────── */

  /* Delegação no document, não listener por seção. A v1.2 fazia
     querySelectorAll("[data-tracking]") no init e anexava um listener em cada
     um — seções injetadas depois (o side cart vem por AJAX de
     templates/cart.ajax_side_cart.liquid) nunca eram rastreadas. */
  function handleGenericClick(e) {
    var section = e.target && e.target.closest ? e.target.closest("[data-tracking]") : null;
    if (!section) return;

    var trackingName = section.getAttribute("data-tracking");
    if (!trackingName) return;

    var eventName = EVENT_MAP[trackingName] || trackingName;
    if (!shouldPush(eventName)) return;

    var target = findClickable(e.target) || e.target;
    var productName = section.getAttribute("data-tracking-product");

    var payload = {
      event: eventName,
      section: trackingName,
      click_text: cleanText(productName || target.innerText || target.getAttribute("aria-label") || ""),
      click_url: clickUrlOf(target),
      click_element: (target.tagName || "").toLowerCase()
    };

    /* posição: primeiro no ancestral do clicado, depois no container da seção */
    var positionEl = e.target.closest ? e.target.closest("[data-tracking-position]") : null;
    var position = positionEl
      ? positionEl.getAttribute("data-tracking-position")
      : section.getAttribute("data-tracking-position");
    if (position) {
      var n = parseInt(position, 10);
      if (!isNaN(n)) payload.click_position = n;
    }

    var extras = EXTRA_PARAMS[trackingName];
    if (extras) {
      for (var key in extras) {
        if (Object.prototype.hasOwnProperty.call(extras, key)) payload[key] = extras[key];
      }
    }

    push(payload);
  }

  /* ─── 2) Side cart do Halo ────────────────────────────────────────────── */

  /* Seletores medidos no DOM REAL (drawer aberto, carrinho populado), não
     inferidos do Liquid: o drawer é injetado por AJAX e o Liquid tem ramos
     condicionais que não rendem nesta configuração de loja.

     `inerte: true` = o evento existe na v1.2 e no GTM, mas HOJE não tem alvo
     neste tema. Mantidos de propósito: se a config mudar, o tracking já
     acompanha, e quem for procurar o evento no GA4 encontra aqui a explicação
     em vez de concluir que o código está quebrado. */
  var CART_MAP = [
    { sel: "[data-cart-sidebar]",                    event: "cart_abrir" },
    { sel: "[data-close-cart-sidebar]",              event: "cart_fechar" },
    { sel: "cart-progressbar, .cart-progressbar",    event: "cart_barra_de_progresso" },
    { sel: "[data-plus-quantity-cart]",              event: "cart_qtd_aumentar" },
    { sel: "[data-minus-quantity-cart]",             event: "cart_qtd_diminuir" },
    { sel: "[data-cart-remove]",                     event: "cart_remover_produto" },
    { sel: "#cart-sidebar-checkout, .button-checkout", event: "cart_cta_checkout" },

    /* inertes nesta configuração — ver comentário acima */
    { sel: ".button-continue",                       event: "cart_continuar_comprando", inerte: true },
    { sel: "[data-update-coupon]",                   event: "cart_cupom_aplicar",       inerte: true },

    /* cart_payments (v1.2) não tem equivalente: este side cart não exibe ícones
       de pagamento. Os únicos do tema estão no footer (snippets/footer-payment.liquid),
       que é outro contexto. Evento intencionalmente não emitido. */
  ];

  var CART_ROOT = ".halo-cart-sidebar";

  /* Nome do produto da linha do carrinho onde o clique aconteceu. */
  function cartProductName(el) {
    if (!el || !el.closest) return "";
    var item = el.closest(".previewCartItem, [data-cart-remove-id]");
    if (!item) return "";
    var link = item.querySelector('a[href*="/products/"]');
    return link ? cleanText(link.innerText) : "";
  }

  /* Marca quando um stepper (+/-) foi clicado. O JS do Halo atualiza o input e
     dispara `change`, então sem isto um clique em "+" emitiria DOIS eventos para
     uma ação só: cart_qtd_aumentar (o clique) e cart_qtd_alterar (o change
     programático). O clique é o evento mais específico e vence. */
  var lastStepperClick = 0;
  var STEPPER_QUIET_MS = 600;

  function handleCartClick(e) {
    if (!e.target || !e.target.closest) return;
    for (var i = 0; i < CART_MAP.length; i++) {
      var entry = CART_MAP[i];
      var hit = e.target.closest(entry.sel);
      if (!hit) continue;
      if (entry.event === "cart_qtd_aumentar" || entry.event === "cart_qtd_diminuir") {
        lastStepperClick = Date.now();
      }
      if (!shouldPush(entry.event)) return;
      push({
        event: entry.event,
        section: "cart",
        click_text: cartProductName(hit) || cleanText(hit.innerText || hit.getAttribute("aria-label") || ""),
        click_url: clickUrlOf(hit),
        click_element: (hit.tagName || "").toLowerCase()
      });
      return;   // um clique = um evento; o primeiro match do mapa vence
    }
  }

  /* cart_qtd_alterar é digitação, não clique: escuta `change` no input.
     Sem isso o evento só existiria no papel — foi o que acontecia na v1.2, que
     mapeava o input dentro do handler de clique. */
  function handleCartQtyChange(e) {
    var input = e.target;
    if (!input || !input.matches) return;
    if (!input.matches("[data-cart-quantity], input.quantity")) return;
    if (!input.closest(CART_ROOT)) return;
    /* change disparado pelo próprio +/- já foi contado como aumentar/diminuir */
    if (Date.now() - lastStepperClick < STEPPER_QUIET_MS) return;
    if (!shouldPush("cart_qtd_alterar")) return;
    push({
      event: "cart_qtd_alterar",
      section: "cart",
      click_text: cartProductName(input),
      click_url: "",
      click_element: "input",
      cart_quantity: parseInt(input.value, 10) || 0
    });
  }

  /* ─── 3) CTA de compra ────────────────────────────────────────────────── */

  /* Sem debounce: o CTA é o evento mais caro de perder, e um duplo clique
     legítimo do cliente é informação, não ruído. Os seletores do Kaching da
     v1.2 saíram (o app não existe aqui); product-form__submit sobrevive e está
     em 9 arquivos deste tema. */
  var CTA_SELECTOR = [
    ".product-form__submit",
    "[data-tracking='product_cta'] button",
    "button.button--primary[type='submit'][name='add']"
  ].join(", ");

  function handleCtaClick(e) {
    if (!e.target || !e.target.closest) return;
    var btn = e.target.closest(CTA_SELECTOR);
    if (!btn) return;
    push({
      event: "product_cta",
      section: "product_cta",
      click_text: cleanText(btn.innerText),
      click_url: "",
      click_element: (btn.tagName || "").toLowerCase()
    });
  }

  /* ─── Bootstrap ───────────────────────────────────────────────────────── */

  /* Um único listener de clique no document cobre os três casos e sobrevive a
     qualquer conteúdo injetado depois. A ordem importa: o carrinho é checado
     antes do motor genérico para que um controle do drawer dentro de uma seção
     com [data-tracking] emita o evento de carrinho, mais específico.

     ⚠️ CAPTURE (o `true` no fim), não bubble. Medido em 13/Ago/2026: o JS do
     Halo chama stopPropagation() nos controles do side cart, então em bubble os
     cliques de abrir, + e − NUNCA chegavam ao document — três eventos sumiam em
     silêncio, e o drawer abria normalmente, o que faz o bug parecer inexistente.
     Em capture nós vemos o clique antes de qualquer handler do tema ou de app
     poder pará-lo. Só observamos: nada aqui altera ou impede o evento. */
  document.addEventListener("click", function (e) {
    handleCartClick(e);
    handleCtaClick(e);
    handleGenericClick(e);
  }, true);

  document.addEventListener("change", handleCartQtyChange, true);

  window.__gdTrackingActive = true;   // sonda para testes automatizados
})();
